import { RASTER_STORE_MEMORY_BUDGET_BYTES } from "./layerTypes";

export interface RasterMemoryRecord {
  blank: boolean;
}

export function estimateRasterSurfaceBytes(width: number, height: number): number {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) {
    return Number.POSITIVE_INFINITY;
  }
  return Math.ceil(width) * Math.ceil(height) * 4;
}

export function canAllocateRasterSurface(
  width: number,
  height: number,
  allocatedSurfaceCount: number,
  budget = RASTER_STORE_MEMORY_BUDGET_BYTES,
): boolean {
  const bytes = estimateRasterSurfaceBytes(width, height);
  return (
    Number.isInteger(allocatedSurfaceCount) &&
    allocatedSurfaceCount >= 0 &&
    Number.isFinite(bytes) &&
    (allocatedSurfaceCount + 1) * bytes <= budget
  );
}

export function estimateAggregateRasterMemoryBytes(
  width: number,
  height: number,
  persistentSurfaceCount: number,
  transientSurfaceCount = 0,
): number {
  const surfaceBytes = estimateRasterSurfaceBytes(width, height);
  const totalSurfaces = persistentSurfaceCount + transientSurfaceCount;
  if (
    !Number.isInteger(persistentSurfaceCount) ||
    persistentSurfaceCount < 0 ||
    !Number.isInteger(transientSurfaceCount) ||
    transientSurfaceCount < 0 ||
    !Number.isFinite(surfaceBytes) ||
    !Number.isSafeInteger(totalSurfaces)
  ) {
    return Number.POSITIVE_INFINITY;
  }
  const totalBytes = surfaceBytes * totalSurfaces;
  return Number.isSafeInteger(totalBytes) ? totalBytes : Number.POSITIVE_INFINITY;
}

export function isAggregateRasterMemoryWithinBudget(
  width: number,
  height: number,
  records: ReadonlyArray<RasterMemoryRecord>,
  transientSurfaceCount = 0,
  budget = RASTER_STORE_MEMORY_BUDGET_BYTES,
): boolean {
  const nonblankSurfaceCount = records.filter((record) => !record.blank).length;
  const bytes = estimateAggregateRasterMemoryBytes(
    width,
    height,
    nonblankSurfaceCount,
    transientSurfaceCount,
  );
  return Number.isFinite(budget) && budget >= 0 && bytes <= budget;
}

export function assertAggregateRasterMemoryWithinBudget(
  width: number,
  height: number,
  records: ReadonlyArray<RasterMemoryRecord>,
  transientSurfaceCount = 0,
  budget = RASTER_STORE_MEMORY_BUDGET_BYTES,
): void {
  if (!isAggregateRasterMemoryWithinBudget(width, height, records, transientSurfaceCount, budget)) {
    throw new Error("This project needs more layer memory than this tablet can safely use.");
  }
}

export function canUseRasterSurfaceBudget(
  width: number,
  height: number,
  persistentSurfaceCount: number,
  transientSurfaceCount = 0,
  budget = RASTER_STORE_MEMORY_BUDGET_BYTES,
): boolean {
  const bytes = estimateAggregateRasterMemoryBytes(
    width,
    height,
    persistentSurfaceCount,
    transientSurfaceCount,
  );
  return Number.isFinite(budget) && budget >= 0 && bytes <= budget;
}

export function canUseRasterSurfaceBudgetWithExtraBytes(
  width: number,
  height: number,
  persistentSurfaceCount: number,
  transientSurfaceCount: number,
  additionalBytes: number,
  budget = RASTER_STORE_MEMORY_BUDGET_BYTES,
): boolean {
  const surfaceBytes = estimateAggregateRasterMemoryBytes(
    width,
    height,
    persistentSurfaceCount,
    transientSurfaceCount,
  );
  if (!Number.isSafeInteger(additionalBytes) || additionalBytes < 0) return false;
  const totalBytes = surfaceBytes + additionalBytes;
  return (
    Number.isSafeInteger(totalBytes) &&
    Number.isFinite(budget) &&
    budget >= 0 &&
    totalBytes <= budget
  );
}

/**
 * Checks live-preview and later commit peaks separately. Additional bytes cover
 * bounded region buffers/surfaces that coexist with each peak.
 */
export function canBeginRasterEditWithPreview(
  width: number,
  height: number,
  allocatedPersistentSurfaceCount: number,
  targetAlreadyAllocated: boolean,
  additionalBytes = 0,
  budget = RASTER_STORE_MEMORY_BUDGET_BYTES,
): boolean {
  if (
    !Number.isInteger(allocatedPersistentSurfaceCount) ||
    allocatedPersistentSurfaceCount < 0 ||
    (targetAlreadyAllocated && allocatedPersistentSurfaceCount < 1)
  ) {
    return false;
  }
  const committedPersistentSurfaceCount =
    allocatedPersistentSurfaceCount + (targetAlreadyAllocated ? 0 : 1);
  const commitFits = canUseRasterSurfaceBudgetWithExtraBytes(
    width,
    height,
    committedPersistentSurfaceCount,
    0,
    additionalBytes,
    budget,
  );
  const livePreviewFits = canUseRasterSurfaceBudgetWithExtraBytes(
    width,
    height,
    allocatedPersistentSurfaceCount,
    1,
    additionalBytes,
    budget,
  );
  return commitFits && livePreviewFits;
}

/**
 * Checks the two distinct stroke peaks without treating a blank target and the
 * live preview as simultaneous allocations. During live input the preview is
 * the one additional surface. At commit, finishStroke releases that preview
 * before a blank target is allocated.
 */
export function canBeginRasterStrokeWithPreview(
  width: number,
  height: number,
  allocatedPersistentSurfaceCount: number,
  targetAlreadyAllocated: boolean,
  budget = RASTER_STORE_MEMORY_BUDGET_BYTES,
): boolean {
  return canBeginRasterEditWithPreview(
    width,
    height,
    allocatedPersistentSurfaceCount,
    targetAlreadyAllocated,
    0,
    budget,
  );
}
